import { isValidDate, serializeDate } from '../utils'

// Generic validators
export const validateRequired = (value: string) => {
  if (!value?.trim()) return 'This field is required'
  return ''
}

export const validateEmail = (value: string) => {
  if (!value) return 'Email is required'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
    return 'Please enter a valid email address'
  return ''
}

export const validatePositiveNumber = (value: string) => {
  if (!value) return 'This field is required'
  const num = Number(value)
  if (isNaN(num)) return 'Must be a valid number'
  if (num < 0) return 'Must be a positive number'
  return ''
}

export const validateYesNo = (value: string | undefined) => {
  if (!value) return 'Please select an option'
  if (!['yes', 'no'].includes(value)) return 'Invalid selection'
  return ''
}

export const validateConfirmation = (value: boolean) => {
  if (!value) return 'You must confirm this statement'
  return ''
}

// Helper function to create enum validators
export const createEnumValidator = <T extends { [key: string]: string }>(
  enumObj: T,
  errorMessage = 'Please make a selection',
) => {
  return (value: string) => {
    if (!value) return errorMessage
    if (!Object.values(enumObj).includes(value)) {
      return 'Invalid selection'
    }
    return ''
  }
}

export const validateName = (value: string) => {
  if (!value) return 'This field is required'
  if (value.length < 2) return 'Must be at least 2 characters'
  if (!/^[a-zA-Z\s-']+$/.test(value))
    return 'Can only contain letters, spaces, hyphens and apostrophes'
  return ''
}

export const validatePassword = (value: string) => {
  if (!value) return 'Password is required'
  // if (value.length < 8) return 'Password must be at least 8 characters'
  // if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter'
  // if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter'
  // if (!/[0-9]/.test(value)) return 'Password must contain at least one number'
  return ''
}

export const validatePPSN = (value: string) => {
  if (!value) return 'PPSN is required'
  const ppsnRegex = /^\d{7}[A-W]([A-IW])?$/
  if (!ppsnRegex.test(value))
    return 'Please enter a valid PPSN (7 digits followed by 1-2 letters)'
  return ''
}

export const validateDateOfBirth = (value: string) => {
  if (!value) return 'Date of birth is required'

  if (!isValidDate(value)) {
    return 'Please enter a valid date (DD.MM.YYYY)'
  }

  const dateObj = new Date(serializeDate(value)!)
  const now = new Date()

  if (dateObj > now) {
    return 'Date cannot be in the future'
  }

  const minDate = new Date()
  minDate.setFullYear(minDate.getFullYear() - 16)

  if (dateObj > minDate) {
    return 'You must be at least 16 years old to register'
  }

  return ''
}

export const validateTermsAccepted = (value: boolean) => {
  if (!value) return 'You must accept the terms and conditions'
  return ''
}

export const validateMonthRange = (value: string) => {
  if (!value) return 'Number of months is required'
  const num = Number(value)
  if (isNaN(num)) return 'Must be a valid number'
  if (num < 1 || num > 12) return 'Must be between 1 and 12 months'
  return ''
}

export const validatePositiveAmount = (value: string) => {
  if (!value) return 'Amount is required'
  const num = Number(value)
  if (isNaN(num)) return 'Must be a valid number'
  if (num < 0) return 'Must be a positive amount'
  return ''
}

export const validateRequiredDate = (value: Date | undefined) => {
  if (!value) return 'Date is required'
  if (value > new Date()) return 'Date cannot be in the future'
  return ''
}

export const validateRequiredSelection = (value: string) => {
  if (!value) return 'Please make a selection'
  return ''
}

export const validateOptionalAmount = (value: string) => {
  if (!value) return ''
  const num = Number(value)
  if (isNaN(num)) return 'Must be a valid number'
  if (num < 0) return 'Must be a positive amount'
  return ''
}

export const validateEircode = (value: string) => {
  if (!value) return 'Eircode is required'
  // Eircode format: A65 F4E2 (routing key: 3 chars, unique identifier: 4 chars)
  // const eircodeRegex = /^[A-Z][0-9][0-9] [A-Z][A-Z][0-9][0-9]$/
  // if (!eircodeRegex.test(value)) return 'Please enter a valid Eircode (e.g. A65 F4E2)'
  return ''
}

export const validateLRTPropertyID = (value: string) => {
  if (!value) return '' // Optional field
  // LRT Property ID is an 8-character alphanumeric string
  const lrtRegex = /^[A-Z0-9]{8}$/
  if (!lrtRegex.test(value))
    return 'Please enter a valid LRT Property ID (8 characters)'
  return ''
}

export const validateAddress = (value: string) => {
  if (!value) return 'Address is required'
  if (value.length < 5) return 'Address must be at least 5 characters long'
  return ''
}

export const validateDateRange = (
  startDate: Date | undefined,
  endDate: Date | undefined,
) => {
  if (!startDate || !endDate) return 'Both start and end dates are required'
  if (startDate > endDate) return 'End date must be after start date'
  return ''
}

export const validateLeaseDuration = (value: string) => {
  if (!value) return 'Lease duration is required'
  const duration = Number(value)
  if (isNaN(duration)) return 'Must be a valid number'
  if (duration < 1) return 'Must be at least 1 month'
  if (duration > 120) return 'Must be less than 120 months'
  return ''
}

export const validateLPTID = (value: string) => {
  if (!value) return 'LPT ID is required'
  // const num = Number(value)
  // if (isNaN(num)) return 'Must be a valid number'
  // if (value.length !== 6) return 'LPT ID must be 6 digits'
  return ''
}

export const validateLoanValue = (value: string) => {
  if (!value) return 'Loan value is required'
  const num = Number(value)
  if (isNaN(num)) return 'Must be a valid number'
  if (num < 60000) return 'Loan value must be at least €60,000'
  if (num > 500000) return 'Loan value must be less than €500,000'
  return ''
}

export const validateDays = (value: string) => {
  if (!value) return 'Number of days is required'
  const num = Number(value)
  if (isNaN(num)) return 'Must be a valid number'
  if (num < 0) return 'Must be a positive number'
  if (num > 365) return 'Cannot exceed 365 days'
  return ''
}

export const validateOptionalDays = (value: string) => {
  if (!value) return ''
  const num = Number(value)
  if (isNaN(num)) return 'Must be a valid number'
  if (num < 0) return 'Must be a positive number'
  if (num > 365) return 'Cannot exceed 365 days'
  return ''
}

export const validateTaxReference = (value: string) => {
  if (!value) return 'Tax reference number is required'
  const num = Number(value)
  if (isNaN(num)) return 'Must be a valid number'
  if (value.length !== 7) return 'Tax reference must be 7 digits'
  return ''
}

export const validateCompanyName = (value: string) => {
  if (!value) return 'Company name is required'
  if (value.length < 2) return 'Company name must be at least 2 characters'
  return ''
}

export const validateDate = (value: string) => {
  if (!value) return 'Date is required'
  const date = new Date(value)
  if (isNaN(date.getTime())) return 'Please enter a valid date'
  if (date > new Date()) return 'Date cannot be in the future'
  return ''
}

export const validateOptionalDate = (value: string) => {
  if (!value) return ''
  const date = new Date(value)
  if (isNaN(date.getTime())) return 'Please enter a valid date'
  if (date > new Date()) return 'Date cannot be in the future'
  return ''
}
